// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { get } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";

import {
  FullName,
  OpportunityType,
  StaffRecord,
  WorkflowsJusticeInvolvedPersonRecord,
} from "~datatypes";

import { AnyWorkflowsSystemConfig } from "../core/models/types";
import { workflowsUrl } from "../core/views";
import {
  ContactMethodType,
  PersonUpdateRecord,
  PersonUpdateType,
} from "../FirestoreStore";
import { RootStore } from "../RootStore";
import { TaskFactory } from "./Client";
import {
  Opportunity,
  OpportunityManagerInterface,
  OpportunityMapping,
} from "./Opportunity";
import { OpportunityManager } from "./Opportunity/OpportunityManager";
import { CollectionDocumentSubscription } from "./subscriptions";
import { SupervisionTaskInterface } from "./Task/types";
import {
  JusticeInvolvedPerson,
  PersonClassForRecord,
  PersonRecordType,
  PersonType,
} from "./types";
import { isEligible } from "./utils";

export abstract class JusticeInvolvedPersonBase<
  RecordType extends PersonRecordType = WorkflowsJusticeInvolvedPersonRecord,
> implements JusticeInvolvedPerson
{
  rootStore: RootStore;

  record: RecordType;

  // Subscription to the `clientUpdatesV2` collection.
  // All JusticeInvolvedPerson updates (both Clients and Residents) are stored in `clientUpdatesv2`,
  // so the name of the collection is misleading, all person updates are stored here.
  personUpdatesSubscription?: CollectionDocumentSubscription<PersonUpdateRecord>;

  opportunityManager: OpportunityManagerInterface;

  abstract systemConfig: AnyWorkflowsSystemConfig;

  abstract personType: PersonType;

  private injectedUpdates?: PersonUpdateRecord;

  constructor(
    record: RecordType,
    rootStore: RootStore,
    taskFactory?: TaskFactory<PersonClassForRecord<RecordType>>,
    /**
     * A SLIM shell subscribes to nothing and runs no task factory. Its
     * identity and overlay both arrive projected inside the opportunity hit,
     * and the per-person subscription is a large part of the fan-out the
     * Typesense path removes.
     */
    options: { slim?: boolean } = {},
  ) {
    this.rootStore = rootStore;

    this.record = record;

    makeObservable<this, "injectedUpdates">(this, {
      record: observable,
      supervisionTasks: observable,
      opportunityManager: observable,
      opportunities: computed,
      opportunitiesAlmostEligible: computed,
      opportunitiesEligible: computed,
      updateRecord: action,
      updates: computed,
      injectedUpdates: observable,
      injectUpdates: action,
      displayPreferredName: computed,
      preferredContactMethod: computed,
      assignedStaff: computed,
      assignedStaffFullName: computed,
      assignedStaffPseudoId: computed,
      district: computed,
    });

    if (!options.slim) {
      this.personUpdatesSubscription =
        new CollectionDocumentSubscription<PersonUpdateRecord>(
          this.rootStore.firestoreStore,
          { key: "clientUpdatesV2" },
          record.recordId,
        );

      this.supervisionTasks = taskFactory
        ? taskFactory(this as unknown as PersonClassForRecord<RecordType>)
        : undefined;
    }

    this.opportunityManager = new OpportunityManager(
      rootStore,
      this,
      this.record.allEligibleOpportunities,
    );
  }
  bannerText?: string | undefined;

  get profileUrl(): string {
    return workflowsUrl("residentProfile", {
      justiceInvolvedPersonId: this.pseudonymizedId,
    });
  }

  get recordId(): string {
    return this.record.recordId;
  }

  get externalId(): string {
    return this.record.personExternalId;
  }

  get pseudonymizedId(): string {
    return this.record.pseudonymizedId;
  }

  get displayId(): string {
    return this.record.displayId;
  }

  get stateCode(): string {
    return this.record.stateCode.toUpperCase();
  }

  get fullName(): FullName {
    return this.record.personName;
  }

  get assignedStaffId(): string | undefined {
    return this.record.officerId ?? undefined;
  }

  get assignedStaff(): StaffRecord | undefined {
    return this.rootStore.workflowsStore?.availableOfficers.find(
      (o) => o.staffExternalId === this.assignedStaffId,
    );
  }

  get district(): string | undefined {
    return this.assignedStaff?.district ?? undefined;
  }

  get assignedStaffFullName(): string | undefined {
    if (!this.assignedStaff) return undefined;
    return [this.assignedStaff.givenNames, this.assignedStaff.surname]
      .join(" ")
      .trim();
  }

  get assignedStaffPseudoId(): string | undefined {
    return this.assignedStaff?.pseudonymizedId;
  }

  get displayName(): string {
    return [this.fullName.givenNames, this.fullName.surname]
      .filter((n) => Boolean(n))
      .join(" ");
  }

  get preferredGivenName(): string {
    return [
      this.fullName.givenNames,
      this.preferredName && this.preferredName !== this.fullName.givenNames
        ? `(${this.preferredName})`
        : undefined,
    ]
      .filter((n) => Boolean(n))
      .join(" ");
  }

  get displayPreferredName(): string {
    return [this.preferredGivenName, this.fullName.surname]
      .filter((n) => Boolean(n))
      .join(" ");
  }

  get displayPreferredNameLastFirst(): string {
    return [this.fullName.surname, this.preferredGivenName]
      .filter((n) => Boolean(n))
      .join(", ");
  }

  get updates(): PersonUpdateRecord | undefined {
    return this.injectedUpdates ?? this.personUpdatesSubscription?.data;
  }

  /**
   * Supplies the person's overlay in place of the subscription.
   * `preferredName` is what matters: it lets `displayPreferredName` resolve on
   * a slim shell.
   */
  injectUpdates(updates: PersonUpdateRecord | undefined): void {
    this.injectedUpdates = updates;
  }

  get preferredName(): string | undefined {
    return this.updates?.preferredName;
  }

  get preferredContactMethod(): ContactMethodType | undefined {
    return this.updates?.preferredContactMethod;
  }

  updatePerson(
    type: PersonUpdateType,
    update: string | ContactMethodType,
  ): Promise<void> {
    return this.rootStore.firestoreStore.updatePerson(this.recordId, {
      [type]: update,
    } as Record<PersonUpdateType, string | ContactMethodType>);
  }

  supervisionTasks?: SupervisionTaskInterface | undefined;

  /**
   * This mapping will only contain opportunities that are actually hydrated and valid;
   * in most cases these are the only ones that should ever be shown to users
   */
  get opportunities(): OpportunityMapping {
    return this.opportunityManager.opportunities;
  }

  get flattenedOpportunities(): Opportunity[] {
    return Object.values(this.opportunities).flat();
  }

  getFilteredOpportunityMapping(
    filterFn: (opp: Opportunity) => boolean,
  ): OpportunityMapping {
    return Object.entries(this.opportunities).reduce(
      (opportunities, [key, oppList]) => {
        const filteredOpps = oppList.filter(filterFn);
        if (filteredOpps.length) {
          return { ...opportunities, [key as OpportunityType]: filteredOpps };
        }
        return opportunities;
      },
      {} as OpportunityMapping,
    );
  }

  get opportunitiesEligible(): OpportunityMapping {
    return this.getFilteredOpportunityMapping((opp) => opp && isEligible(opp));
  }

  get opportunitiesAlmostEligible(): OpportunityMapping {
    return this.getFilteredOpportunityMapping(
      (opp) => opp && opp.almostEligible && !opp.isSubmitted && !opp.denied,
    );
  }

  get opportunitiesDenied(): OpportunityMapping {
    return this.getFilteredOpportunityMapping((opp) => opp && opp.denied);
  }

  updateRecord(newRecord: RecordType): void {
    this.record = newRecord;
  }

  trackProfileViewed(): void {
    this.rootStore.analyticsStore.trackProfileViewed({
      justiceInvolvedPersonId: this.pseudonymizedId,
    });
  }

  // These are all of the possible search id values by search field, even if that search id value is not in selectedSearchIds,
  get searchIdValues(): string[] {
    return Object.values(this.searchIdValuesBySearchField).flat();
  }

  // These are all of the searchIdValues, broken out by searchField.
  get searchIdValuesBySearchField(): Record<string, string[]> {
    return this.systemConfig.search.reduce(
      (acc: Record<string, string[]>, searchConfig) => {
        acc[searchConfig.searchField.join(".")] = [
          get(this.record, searchConfig.searchField),
        ].flat();
        return acc;
      },
      {},
    );
  }
}
